import type { QuoteType, ResidentialItemType } from './types';
import { getRequiredRateSlugs } from './formulaReadiness';
import { isPricedCategory } from './utils';

/**
 * What an org has not priced yet, checked when someone starts a new estimate.
 * A quote started without these comes out under-priced: the estimator then has
 * to set them by hand on the quote (or start over once an admin sets them).
 */
export interface OrgPricingGaps {
  /** A crew / truck rate the formula bills is $0 or missing. */
  crewRates: boolean;
  /** The org has packing items / equipment but none of them has a unit cost. */
  materialPrices: boolean;
}

export interface OrgPricingGapsInput {
  /** The move types the new estimate could be (the form's type options). */
  moveTypes: QuoteType[];
  rates: ReadonlyArray<{ slug: string; hourlyRate: number }>;
  useBlendedTruckDriverRate?: boolean;
  residentialPricingModel?: 'flat_rate' | 'hourly_rate';
  items: ReadonlyArray<{ residentialType?: ResidentialItemType | null; quoteType?: QuoteType; unitCost?: number | null }>;
}

export function findOrgPricingGaps(input: OrgPricingGapsInput): OrgPricingGaps {
  const rateBySlug = new Map(input.rates.map(r => [r.slug, r.hourlyRate]));
  // Residential flat-rate quotes are priced from the bedroom tiers, not crew rates.
  const crewRates = input.moveTypes.some(type =>
    !(type === 'residential' && input.residentialPricingModel === 'flat_rate')
    && getRequiredRateSlugs(type, input.useBlendedTruckDriverRate).some(slug => !((rateBySlug.get(slug) ?? 0) > 0)),
  );
  const materialPrices = input.moveTypes.some(type => {
    const materials = input.items.filter(i => isPricedCategory(i.residentialType) && (i.quoteType ?? 'commercial') === type);
    return materials.length > 0 && !materials.some(i => (i.unitCost ?? 0) > 0);
  });
  return { crewRates, materialPrices };
}

/** "crew rates", "packing item prices" or "crew rates and packing item prices"; null when nothing is missing. */
export function describeOrgPricingGaps(gaps: OrgPricingGaps): string | null {
  if (gaps.crewRates && gaps.materialPrices) return 'crew rates and packing item prices';
  if (gaps.crewRates) return 'crew rates';
  if (gaps.materialPrices) return 'packing item prices';
  return null;
}
